/**
 * Visões da Plataforma (redesign por perfil, Fase 0).
 *
 * NÃO é controle de acesso. A Plataforma já tem um papel real de staff
 * (`StaffSessionState.role`, `admin`/`operator`, lido de `app_metadata` via
 * Supabase Auth — ver `auth/session.ts`) — esse continua sendo o ÚNICO limite
 * de segurança do app. "Visão" aqui é só uma preferência de apresentação:
 * qual recorte de `NAV_GROUPS` a sidebar mostra por padrão, trocável a
 * qualquer momento por quem estiver logado, sem relação com o que a pessoa
 * TEM PERMISSÃO de ver (ela sempre tem — é só densidade de menu). Por isso o
 * default é sempre "completa": reduzir o menu é opt-in, nunca o estado
 * inicial de ninguém.
 *
 * Mantém o mesmo princípio dos indicadores do dashboard (nunca inventar dado
 * que não existe): como o app não tem papéis granulares (só admin/operator),
 * as visões abaixo são recortes editoriais dos grupos de módulo que já
 * existem em `AppShell.ts` (`NAV_GROUPS`), não um novo modelo de permissão.
 */
import type { LucideIcon } from "lucide-react";
import { Briefcase, LayoutGrid, Settings, ShoppingBag, Warehouse } from "lucide-react";

export type ViewId = "completa" | "executiva" | "vendas-atendimento" | "estoque-logistica" | "sistema";

export interface ViewDef {
  id: ViewId;
  label: string;
  description: string;
  icon: LucideIcon;
  /** ids de `NAV_GROUPS`; `null` = todos os grupos (sem filtro). */
  groupIds: string[] | null;
}

export const VIEWS: ViewDef[] = [
  {
    id: "completa",
    label: "Visão completa",
    description: "Todos os módulos da Plataforma.",
    icon: LayoutGrid,
    groupIds: null
  },
  {
    id: "executiva",
    label: "Executiva",
    description: "Visão geral e financeiro — números de resultado.",
    icon: Briefcase,
    groupIds: ["visao-geral", "financeiro"]
  },
  {
    id: "vendas-atendimento",
    label: "Vendas & Atendimento",
    description: "Visão geral, vendas e atendimento/CRM.",
    icon: ShoppingBag,
    groupIds: ["visao-geral", "vendas", "atendimento-crm"]
  },
  {
    id: "estoque-logistica",
    label: "Estoque & Logística",
    description: "Catálogo, estoque e logística.",
    icon: Warehouse,
    groupIds: ["catalogo-estoque", "logistica"]
  },
  {
    id: "sistema",
    label: "Sistema",
    description: "Configurações e integrações.",
    icon: Settings,
    groupIds: ["sistema"]
  }
];

/** Default de todo mundo: nenhum módulo escondido até a pessoa escolher outra visão. */
export const DEFAULT_VIEW_ID: ViewId = "completa";

export function getView(id: ViewId): ViewDef {
  return VIEWS.find((view) => view.id === id) ?? VIEWS[0];
}

/** `true` se o grupo `groupId` aparece na visão `viewId` (visão "completa" nunca filtra). */
export function groupVisibleInView(groupId: string, viewId: ViewId): boolean {
  const view = getView(viewId);
  return view.groupIds === null || view.groupIds.includes(groupId);
}

export function isViewId(value: string | null): value is ViewId {
  return value !== null && VIEWS.some((view) => view.id === value);
}
